import React, { ChangeEvent, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SearchFilled } from '@tag/tag-icons';
import { debounce } from 'lodash';

import useKeypress from '@/hooks/useKeypress.hook';
import { useOnClickOutside } from '@/hooks/useOnClickOutside.hook';
import MainSearchResult from './MainSearchResult';
import { SEARCH_DELAY_MS } from '@/config/common.config';
import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import styles from './main-search.module.scss';

interface IMainSearchProps {
  placeholder?: string,
  loading?: boolean,
  handleChange: (text: string) => void,
  data: any;
}

export default function MainSearch({
  placeholder,
  loading = false,
  handleChange,
  data
}: IMainSearchProps): JSX.Element {
  const [isFocusedInput, setIsFocusedInput] = useState<boolean>(true);
  const [text, setText] = useState<string>('');
  const mainSearchRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const onChangeText = (event: ChangeEvent<HTMLInputElement>) => {
    setIsFocusedInput(true);
    setText(event.target.value);
    const delaySearchResults = debounce(() => handleChange(event.target.value), SEARCH_DELAY_MS);
    delaySearchResults();

  };

  const onFocusInput = () => {
    setIsFocusedInput(true);
  };

  const blurInput = () => {
    setIsFocusedInput(false);
  };

  useOnClickOutside(mainSearchRef, () => {
    setIsFocusedInput(false);
  });

  useKeypress('Escape', () => {
    setIsFocusedInput(false);
  });

  useEffectExceptOnMount(() => {
    setText('');
  }, [location]);

  return (
    <div className={styles['main-search']} ref={mainSearchRef}>
      <div className={styles['search-field']}>
        <div className={styles['icon-wrapper']}>
          {loading ? (
            <span className={styles.spinner} />
          ) : (
            <SearchFilled />
          )}
        </div>
        <input
          placeholder={placeholder}
          type="text"
          className={styles.input}
          onChange={onChangeText}
          onFocus={onFocusInput}
          value={text}
        />
      </div>

      {isFocusedInput && data && !loading ? (
        <MainSearchResult
          blurInput={blurInput}
          data={data}
        />
      ) : null}

    </div>
  );
}
